import React, { FC, useRef, useState, useEffect } from 'react';

import Stack from '@mui/material/Stack';
import DeleteIcon from '@mui/icons-material/Delete';
import Typography from '@mui/material/Typography';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';

import { DocumentProperties } from '@/domain/entities/ParcelDocument';
import { toFormattedDateDDMMYY } from '@/utils';

export type DocumentItemProps = {
  document: DocumentProperties;
  onDownload: (dataUrl: string, name: string) => void;
  onDelete: (uuid: string) => void;
};

const DocumentItem: FC<DocumentItemProps> = ({ document, onDownload, onDelete }) => {
  const { uuid, createdAt, description, name, dataUrl } = document;

  // REF para detectar truncado
  const descRef = useRef<HTMLSpanElement | null>(null);
  const [isTruncated, setIsTruncated] = useState(false);

  useEffect(() => {
    const el = descRef.current;
    if (!el) return;

    const truncated = el.scrollHeight > el.clientHeight + 1;
    setIsTruncated(truncated);
  }, [description]);

  const handleDownload = () => onDownload(dataUrl, name);
  const handleDelete = () => onDelete(uuid);

  return (
    <Stack direction="row" sx={styles.container}>
      <Stack direction="row" onClick={handleDownload} sx={styles.downloadContainer}>
        <Stack direction="column" sx={{ flex: 1, minWidth: 0 }}>
          {/* Name con truncado en 1 línea y tooltip */}
          <Tooltip title={name}>
            <Typography sx={styles.file}>{name}</Typography>
          </Tooltip>

          {/* Fecha */}
          <Typography sx={styles.description}>{toFormattedDateDDMMYY(new Date(createdAt))}</Typography>

          {/* Description con clamp + tooltip */}
          {isTruncated ? (
            <Tooltip title={description} placement="top">
              <Typography
                ref={descRef}
                component="span"
                sx={{ ...styles.description, display: '-webkit-box' }}
              >
                {description}
              </Typography>
            </Tooltip>
          ) : (
            <Typography ref={descRef} component="span" sx={styles.description}>
              {description}
            </Typography>
          )}
        </Stack>
      </Stack>

      <IconButton onClick={handleDelete}>
        <DeleteIcon sx={styles.deleteIcon} />
      </IconButton>
    </Stack>
  );
};

export default DocumentItem;

const styles = {
  container: {
    bgcolor: 'grey.200',
    borderRadius: 1,
    p: 1,
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    width: '100%',
    minWidth: 0,
    mb: 1,
    '&:hover': {
      bgcolor: 'grey.300',
    },
  },
  downloadContainer: {
    gap: 1,
    flex: 1,
    display: 'flex',
    minWidth: 0,
    cursor: 'pointer',
    width: '80%',
  },
  file: {
    color: 'text.primary',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    width: '100%',
  },
  description: {
    display: '-webkit-box',
    WebkitLineClamp: 2, // máximo 2 líneas
    WebkitBoxOrient: 'vertical',
    overflow: 'hidden',
    lineHeight: '1.4rem',
    maxHeight: '2.8rem', // 2 * lineHeight
    color: 'grey.600',
    fontStyle: 'italic',
  },
  deleteIcon: {
    color: 'grey.700',
  },
};
